'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/neural-nets'

const MLPPlayground = dynamic(() => import('../_components/widgets/MLPPlayground'), { ssr: false })

export default function TrainingNetworks() {
  return (
    <Lesson>
      <Step>
        <H>A network playground</H>
        <P>
          Everything comes together here: a real neural network, trained live with backpropagation. Each small square is one
          neuron, showing what it responds to across the whole input plane. The big square is the network’s final answer, with{' '}
          <strong>training points filled</strong> and <strong>test points hollow</strong>.
        </P>
        <Figure n="7.6" wide caption="Press Train. Then experiment: switch to the spiral, remove the hidden layers entirely, add neurons, change the activation, crank the learning rate. Watch the hidden neurons — each one learns a different ‘feature’.">
          <MLPPlayground />
        </Figure>
      </Step>

      <Step gate={['q-spiral', 'q-depth']}>
        <H>Experiments to try</H>
        <ul className="gd-list">
          <li>
            <strong>Remove every hidden layer</strong> (“− layer” until 0). The network becomes logistic regression: one straight
            boundary. The circle becomes impossible.
          </li>
          <li>
            <strong>Spiral with one small layer</strong> vs. <strong>spiral with three layers</strong>. Depth matters.
          </li>
          <li>
            <strong>Learning rate 0.3.</strong> The loss starts jumping around — steps too big.
          </li>
          <li>
            <strong>Lots of noise and lots of neurons.</strong> Watch train loss keep falling while test loss stalls or rises:
            overfitting.
          </li>
        </ul>
        <Quiz
          id="q-spiral"
          question="With zero hidden layers, why does the network fail on the circle dataset?"
          options={[
            { label: 'It can only draw a straight-line boundary, and no line separates inside from outside a circle.', correct: true },
            { label: 'It doesn’t train for long enough.', why: 'Train it as long as you like — a single line can’t enclose a region.' },
            { label: 'The learning rate is wrong.', why: 'No learning rate can make a linear model draw a curve.' },
          ]}
        />
        <Quiz
          id="q-depth"
          question="Looking at the small squares in a trained network, what do the neurons in the first hidden layer typically show?"
          options={[
            { label: 'Simple straight-ish splits of the plane, which later layers combine into curved shapes.', correct: true },
            { label: 'The final answer, repeated.', why: 'Each early neuron captures only a piece; the combination is what forms the answer.' },
            { label: 'Random noise.', why: 'At initialisation, perhaps — but training turns each one into a useful feature.' },
          ]}
          explain="This is the essence of deep learning: early layers learn simple features, later layers compose them. In image models, early layers detect edges; later ones, eyes and wheels."
        />
      </Step>

      <Step gate="train-mlp">
        <H>Train one in code</H>
        <P>
          Now train a network yourself with the <C>MLP</C> and <C>Value</C> classes. This is the complete recipe used for every
          neural network: forward, loss, zero the gradients, backward, update — over and over.
        </P>
        <Exercise
          id="train-mlp"
          title="A neural network on two moons"
          {...EX.trainMlp}
          timeoutMs={60000}
          outputHeight={420}
          hints={[
            <>
              Inside the epoch loop: <C>model.zeroGrad()</C>, then build an array of per-example losses with{' '}
              <C>{'d.X.map((x, i) => { const logit = model.forward(x)[0]; return nn.crossEntropy([logit.neg(), logit], d.y[i]) })'}</C>.
            </>,
            <>
              <C>const loss = Value.sum(losses).div(d.X.length)</C>, then <C>loss.backward()</C>, <C>nn.sgd(params, lr)</C>, and{' '}
              <C>chart.push(loss.data)</C>.
            </>,
          ]}
        />
        <Callout kind="note">
          Why <C>[logit.neg(), logit]</C>? Softmax over two scores <C>[−z, z]</C> gives class 1 the probability{' '}
          <C>σ(2z)</C> — a sigmoid in disguise. It lets us reuse the multi-class cross-entropy for a yes/no problem.
        </Callout>
      </Step>

      <Step>
        <H>Practical wisdom</H>
        <ul className="gd-list">
          <li>
            <strong>Initialise randomly.</strong> If every weight starts equal, every neuron computes the same thing and gets
            the same gradient forever. Random starts break the symmetry. (Scale matters too: too big and tanh saturates; too small
            and signals vanish.)
          </li>
          <li>
            <strong>Watch the loss curve.</strong> Smoothly falling: good. Flat from the start: learning rate too small or a bug.
            Exploding or NaN: learning rate too big.
          </li>
          <li>
            <strong>Overfit a tiny dataset first.</strong> If your network can’t memorise 10 examples, something is broken.
          </li>
          <li>
            <strong>Normalise inputs.</strong> Features on wildly different scales make the loss landscape a narrow ravine.
          </li>
        </ul>
      </Step>
    </Lesson>
  )
}
